import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Popover, TextField, Typography } from "@mui/material";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./RichTextEditor.css";
import { VariableOption, filterVariableOptions } from "./VariablePicker";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Recebe a função que insere texto no ponto do cursor (ou no fim), para botões fora do editor. */
  insertRef?: React.MutableRefObject<((text: string) => void) | null>;
  /** Com a lista, digitar "{" abre a busca de variáveis no ponto do cursor (troca o "{" pela escolhida). */
  suggestions?: VariableOption[];
}

// Wrapper do Quill 2 (JS nativo) para React 19 — sem react-quill (incompatível com React 19).
// Inicializa uma vez; o conteudo inicial vem de `value`. Para trocar de template, remontar via key.

// Fontes disponiveis (registradas no whitelist do Quill; estilo aplicado em global.css).
const FONT_WHITELIST = [
  "arial",
  "times-new-roman",
  "courier-new",
  "georgia",
  "verdana",
  "tahoma",
];

// Tamanhos de fonte (atributo inline de estilo -> font-size vai no HTML, refletindo no preview/impressao).
const SIZE_WHITELIST = [
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Font = Quill.import("formats/font") as any;
Font.whitelist = FONT_WHITELIST;
Quill.register(Font, true);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Size = Quill.import("attributors/style/size") as any;
Size.whitelist = SIZE_WHITELIST;
Quill.register(Size, true);

// Espacamento entre linhas (line-height) — atributo de estilo customizado (nao nativo do Quill).
const LINE_HEIGHT_WHITELIST = ["1", "1.15", "1.5", "2", "2.5", "3"];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Parchment = Quill.import("parchment") as any;
const LineHeight = new Parchment.StyleAttributor(
  "lineHeight",
  "line-height",
  {
    scope: Parchment.Scope.BLOCK,
    whitelist: LINE_HEIGHT_WHITELIST,
  }
);
Quill.register({ "formats/lineHeight": LineHeight }, true);

// Paleta ampliada de cores (texto e fundo), incluindo tons pastel.
const COLOR_PALETTE = [
  // tons de cinza
  "#000000", "#434343", "#666666", "#999999", "#b7b7b7", "#cccccc", "#d9d9d9", "#efefef", "#ffffff",
  // cores fortes
  "#e60000", "#ff9900", "#ffff00", "#00b050", "#0070c0", "#7030a0", "#c00000", "#ed7d31", "#ffc000",
  // tons medios
  "#a10000", "#b26b00", "#b2b200", "#006100", "#0047b2", "#52307c", "#833c00", "#9c6500", "#7f6000",
  // pastel 1
  "#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3", "#cfe2f3", "#d9d2e9", "#ead1dc", "#fde2dd", "#e6f2d5",
  // pastel 2
  "#ffd1dc", "#ffe4c4", "#fff5ba", "#c1f0c1", "#b5e7f7", "#d7bde2", "#f9c6d3", "#ffdac1", "#c7ceea",
  // pastel 3
  "#b5ead7", "#e2f0cb", "#f1cbff", "#d0f4de", "#fbe7c6", "#a0e7e5", "#fbc4ab", "#cdb4db", "#bde0fe",
];

const TOOLBAR = [
  // `false` = tamanho padrao (16px, base do .ql-editor): sem ele o seletor mostrava o primeiro
  // item (10px) como se fosse o tamanho do texto, e nao era.
  [{ font: FONT_WHITELIST }, { size: [false, ...SIZE_WHITELIST] }, { header: [1, 2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ color: COLOR_PALETTE }, { background: COLOR_PALETTE }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }, { lineHeight: LINE_HEIGHT_WHITELIST }],
  ["link", "image"],
  ["clean"],
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  insertRef,
  suggestions,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;
  // Posição do "{" digitado e onde abrir a busca (coordenadas da tela)
  const [trigger, setTrigger] = useState<{ index: number; top: number; left: number } | null>(null);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const filtered = useMemo(() => filterVariableOptions(suggestions ?? [], query), [suggestions, query]);

  useEffect(() => {
    if (!containerRef.current || quillRef.current) {
      return;
    }

    const quill = new Quill(containerRef.current, {
      theme: "snow",
      placeholder,
      modules: { toolbar: TOOLBAR },
    });
    quillRef.current = quill;

    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
    }

    quill.on("text-change", (delta, _old, source) => {
      const html = quill.root.innerHTML;
      onChangeRef.current(html === "<p><br></p>" ? "" : html);

      if (source !== "user" || !suggestionsRef.current?.length) return;
      const ops = delta.ops ?? [];
      const last = ops[ops.length - 1];
      if (!last || last.insert !== "{") return;
      const index = (ops.length > 1 && typeof ops[0].retain === "number" ? ops[0].retain : 0);
      const bounds = quill.getBounds(index);
      const rect = quill.root.getBoundingClientRect();
      if (!bounds) return;
      setQuery("");
      setHighlight(0);
      setTrigger({ index, top: rect.top + bounds.top + bounds.height, left: rect.left + bounds.left });
    });

    if (insertRef) {
      insertRef.current = (text: string) => {
        // getSelection(true) devolve o foco e a última posição do cursor, perdida ao clicar no botão
        const range = quill.getSelection(true);
        const index = range ? range.index : Math.max(quill.getLength() - 1, 0);
        quill.insertText(index, text, "user");
        quill.setSelection(index + text.length, 0, "user");
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeTrigger = (option: VariableOption | null) => {
    const quill = quillRef.current;
    const at = trigger;
    setTrigger(null);
    if (!quill || !at) return;
    if (option) {
      // Troca o "{" digitado pela variável inteira
      quill.deleteText(at.index, 1, "user");
      quill.insertText(at.index, option.token, "user");
      quill.setSelection(at.index + option.token.length, 0, "user");
    } else {
      // Desistiu: o "{" fica, e o cursor volta para depois dele
      quill.setSelection(at.index + 1, 0, "user");
    }
  };

  return (
    <div
      style={{
        border: "1px solid #ced4da",
        borderRadius: 6,
        background: "#fff",
      }}
    >
      <div ref={containerRef} style={{ minHeight: 320 }} />
      <Popover
        open={trigger !== null}
        anchorReference="anchorPosition"
        anchorPosition={trigger ? { top: trigger.top, left: trigger.left } : undefined}
        onClose={() => closeTrigger(null)}
        disableRestoreFocus
        PaperProps={{ sx: { p: 1, width: 360, maxWidth: "calc(100vw - 32px)", borderRadius: "8px" } }}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={query}
          placeholder="Buscar variável (Esc mantém o { digitado)"
          onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); closeTrigger(filtered[highlight] ?? null); }
            else if (e.key === "Escape") { e.preventDefault(); closeTrigger(null); }
          }}
        />
        <Box role="listbox" sx={{ mt: 0.5, maxHeight: 280, overflowY: "auto" }}>
          {filtered.length === 0 && (
            <Typography sx={{ fontSize: "0.8rem", color: "#6c757d", px: 1, py: 1.5 }}>Nenhuma variável com esse nome</Typography>
          )}
          {filtered.map((option, i) => {
            const showGroup = i === 0 || filtered[i - 1].group !== option.group;
            return (
              <React.Fragment key={option.token}>
                {showGroup && (
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#6c757d", px: 1, pt: 1, pb: 0.25, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    {option.group}
                  </Typography>
                )}
                <Box
                  role="option"
                  aria-selected={i === highlight}
                  ref={(el: HTMLDivElement | null) => { if (el && i === highlight) el.scrollIntoView({ block: "nearest" }); }}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => { e.preventDefault(); closeTrigger(option); }}
                  sx={{ px: 1, py: 0.75, borderRadius: "4px", cursor: "pointer", backgroundColor: i === highlight ? "#f0f9fa" : "transparent" }}
                >
                  <Typography sx={{ fontSize: "0.85rem", color: "#212529" }}>{option.label}</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#6c757d", fontFamily: "monospace", wordBreak: "break-all" }}>
                    {option.token}{option.example ? ` · ex.: ${option.example}` : ""}
                  </Typography>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Popover>
    </div>
  );
};

export default RichTextEditor;
